import { useContext, useEffect, useState } from "react"
import { ChevronDownIcon, ChevronUpIcon, SignalIcon, SquaresPlusIcon } from "@heroicons/react/24/outline"
import { Panel } from "~/components/CommandMenu/Panel"
import { MultiContext } from "~/contexts/MultiContext"
import { validateNewId } from "./utils"

const PREVIEW_COUNT = 2

export function Add() {
  const { addVideo } = useContext(MultiContext)
  const [newId, setNewId] = useState("")
  const [liveStreams, setLiveStreams] = useState([])
  const [liveLoading, setLiveLoading] = useState(true)

  useEffect(() => {
    fetch("/channels/live")
      .then((r) => r.json())
      .then(({ streams }) => setLiveStreams(streams || []))
      .catch(() => {})
      .finally(() => setLiveLoading(false))
  }, [])

  const effectiveId = validateNewId(newId)

  const onClick = ({ position }) => {
    addVideo(position, effectiveId)
    setNewId("")
  }

  return (
    <Panel>
      <Panel.Heading>
        <Panel.HeadingDescription>
          Paste a link or YouTube video ID, then select a position to add it
        </Panel.HeadingDescription>
        <Panel.HeadingForm id="video-id" value={newId} onChange={setNewId} />
      </Panel.Heading>

      <LiveSection
        loading={liveLoading}
        streams={liveStreams}
        onSelect={(videoId) => setNewId(`https://www.youtube.com/watch?v=${videoId}`)}
      />

      <Panel.Layout Icon={SquaresPlusIcon} onClick={onClick} enabledOptions="inactive" />
    </Panel>
  )
}

function groupByChannel(streams) {
  const groups = []
  const index = new Map()
  for (const stream of streams) {
    if (!index.has(stream.channel_id)) {
      const group = { channel_id: stream.channel_id, channel_name: stream.channel_name, streams: [] }
      groups.push(group)
      index.set(stream.channel_id, group)
    }
    index.get(stream.channel_id).streams.push(stream)
  }
  return groups
}

function LiveSection({ loading, streams, onSelect }) {
  const [expanded, setExpanded] = useState(new Set())

  if (loading) {
    return <div className="mb-4 text-sm text-gray-400">Checking for live streams…</div>
  }

  if (!streams.length) {
    return (
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-400">
        <SignalIcon className="size-4 flex-none" />
        Nothing live right now
      </div>
    )
  }

  const groups = groupByChannel(streams)

  const toggle = (channelId) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(channelId) ? next.delete(channelId) : next.add(channelId)
      return next
    })

  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <SignalIcon className="size-3.5" />
        Live Now
      </div>
      {groups.map((group) => {
        const isExpanded = expanded.has(group.channel_id)
        const visible = isExpanded ? group.streams : group.streams.slice(0, PREVIEW_COUNT)
        const hidden = group.streams.length - PREVIEW_COUNT

        return (
          <div key={group.channel_id}>
            {visible.map((stream) => (
              <StreamRow key={stream.video_id} stream={stream} onSelect={onSelect} />
            ))}
            {hidden > 0 && (
              <button
                onClick={() => toggle(group.channel_id)}
                className="flex w-full items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="size-3" />
                    Show less from {group.channel_name}
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="size-3" />
                    {hidden} more from {group.channel_name}
                  </>
                )}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function StreamRow({ stream, onSelect }) {
  return (
    <button
      onClick={() => onSelect(stream.video_id)}
      className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-900/5"
    >
      {stream.thumbnail && (
        <img src={stream.thumbnail} alt="" className="h-10 w-[72px] flex-none rounded object-cover" />
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900">{stream.title}</p>
        <p className="truncate text-xs text-gray-500">
          {stream.channel_name}
          {stream.viewer_count > 0 && ` · ${stream.viewer_count.toLocaleString()} watching`}
        </p>
      </div>
    </button>
  )
}
